import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../services/language'
import { MessageService } from '../../services/message.service';
import { MasterService } from '../../services/master.service';
import { StoreService } from '../../services/store.service';
import moment from 'moment';
import { ago, daysAgo } from 'src/app/utils/date';
import { SCROLL_BOTTOM_OUT, SEARCH_DEBOUNCE_MILLIS } from 'src/app/constants/list';
import { Subscription } from 'rxjs';

@Component({
    selector: 'all-accounts',
    templateUrl: './all-accounts.component.html',
    styleUrls: ['./all-accounts.component.scss'],
    standalone: false
})
export class AllAccountsComponent implements OnInit, OnDestroy {

    private FILTER_INQUIRY_KEY = 'flt-accts-q'
    private FILTER_TYPE_KEY = 'flt-accts-t'
    private FILTER_TAB_KEY = 'flt-accts-tab'
    private TAB_NAMES = ['active', 'inactive']

    // Unique internal identity of the lot whose past due payments will be queried for.
    @Input() lotId: number = 0

    // Unique internal identity of the contact whose past due payments will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Unique internal identity of the sale finance type to filter the queried past due payments with.
    @Input() financeTypeId: number = 0

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    /** Notify other components that a past due account was chosen by the user. */
    @Output() accountChosen: EventEmitter<Object> = new EventEmitter()

    //
    protected accounts: Array<any> = []

    //
    public records: number = 0

    //
    public page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    public exhausted: boolean = false

    // The currently selected tersm (receivable) identity
    protected termsId: number = 0

    // The current selected tab (active, inactive)
    protected activeTab: string = 'active'

    // The form to handle search inquires
    protected search: FormGroup;

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for sub component events and react if needed. */
    private componentListener: Subscription | null = null

    private subscriptions: Subscription[] = []

    constructor (private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder) {
        this.search = this.fb.group({
            inquiry: new FormControl<string>('', []),
            type: new FormControl<number>(this.financeTypeId, [])
        })
    }

    ngOnInit() {
        this.readFilters()
        this.fetch()

        this.subscriptions.push(
            this.search.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }
        
                this.watcher = setTimeout(() => {
                    this.writeFilters()
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.search.get('type').valueChanges.subscribe(_ => {
                this.writeFilters()
                this.reset()
                this.fetch()
            })
        )

        this.componentListener = this.ms.channelComponents$.subscribe(res => {
            if (!res || !res.message) {
                return
            }

            switch (res.message) {
                case 'saleWrittenOff':
                case 'saleRepossesed':
                case 'saleReactivated':
                    this.reset()
                    this.fetch()
                    break
            }
        })
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }

        this.subscriptions = []
        
        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        if (this.componentListener) {
            this.componentListener.unsubscribe()
            this.componentListener = null
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId || changes.contactId || changes.financeTypeId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
            lotParam = ''
        }

        let financeTypeParam = ''
        let typeId  = this.financeTypeId
        if (!typeId || (typeId <= 0)) {
            typeId = parseInt(this.search.get('type').value)
        }

        if (typeId && (typeId > 0)) {
            financeTypeParam = `&financeType=${typeId}`
        }

        let inquiryParam = ''
        const inquiry = this.search.get('inquiry').value.trim()
        if (inquiry && (inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(inquiry)}`
        }

        let inactiveParam = ''
        if (this.activeTab && (this.activeTab === 'inactive')) {
            inactiveParam = '&inactive=true'
        }

        this.loading = true
        if (this.page === 1) {
            this.accounts = []
            this.termsId = 0
        }

        this.master.get(`receivables?page=${this.page}&sort=name|ASC${lotParam}${contactParam}${financeTypeParam}${inquiryParam}${inactiveParam}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.constructAccounts(res.data.receivables)
                return
            }
  
            if (!res.data.receivables || (res.data.receivables.length <= 0)) {
                return
            }

            this.constructAccounts(res.data.receivables)

            // choose the first available workable account
            let payment = res.data.receivables.find((pay) => pay.workable)
            if (!payment) {
                payment = res.data.receivables[0]
            }

            const meta = {
                termsId: parseInt(payment['termsId']),
                saleId: parseInt(payment['saleId']),
                contactId: parseInt(payment['contactId'])
            }

            this.termsId = meta.termsId
            this.accountChosen.emit(meta)
        })
    }

    /**
     * Using the payments data we received lets build a list 
     * of past due accounts we can use to quickly populate 
     * the UI listing.
     * @param {Array} receivables A list of receivables to append to the past due list.
     */
    private constructAccounts = (receivables) => {
        if (!receivables || (receivables.length <= 0)) {
            return
        }

        for (const receivable of receivables) {
            const account = {
                termsId: parseInt(receivable.termsId || 0),
                saleId: parseInt(receivable.saleId || 0),
                contactId: parseInt(receivable.contactId || 0),
                contact: receivable.contact,
                stock: receivable.stock,
                vehicle: receivable.vehicle,
                url: receivable.url,
                vin: receivable.vin,
                category: receivable.category,
                status: receivable.status,
                group: receivable.group,
                due: receivable.due,
                amount: 0,
                worked: receivable.worked,
                workable: receivable.workable,
                insurance: this.insuranceMessage(receivable),
                insuranceClass: this.insuranceClass(receivable),
                categories: [],
                items: []
            }

            if (receivable.deposit && receivable.depositDue && (ago(receivable.depositDue) === 'past')) {
                account.items.push({
                    category: 'deposit',
                    amount: receivable.deposit,
                    due: receivable.depositDue
                })

                account.amount += receivable.deposit
                account.categories.push('deposit')
            }

            if (receivable.initial && receivable.initialDue && (ago(receivable.initialDue) === 'past')) {
                account.items.push({
                    category: 'initial down',
                    amount: receivable.initial,
                    due: receivable.initialDue
                })

                account.amount += receivable.initial
                account.categories.push('initial down')
            }

            if (receivable.deferred && receivable.deferredDue && (ago(receivable.deferredDue) === 'past')) {
                account.items.push({
                    category: 'deferred down',
                    amount: receivable.deferred,
                    due: receivable.deferredDue
                })

                account.amount += receivable.deferred
                account.categories.push('deferred down')
            }

            if (receivable.loan && receivable.loanDue && (ago(receivable.loanDue) === 'past')) {
                account.items.push({
                    category: 'mv loan',
                    amount: receivable.loan,
                    due: receivable.loanDue
                })

                account.amount += receivable.loan
                account.categories.push('mv loan')
            }

            this.accounts.push(account)
        }
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    protected insuranceMessage = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'no insurance'
        }

        if (receivable.insuranceExpires && receivable.insuranceIsCpi) {
            return this.cpiMessage(receivable.insuranceExpires)
        }

        return this.insMessage(receivable.insuranceProvider, receivable.insuranceExpires)
    }

    protected insuranceClass = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'danger'
        }

        return this.insClass(receivable.insuranceExpires)
    }

    private cpiMessage = (ends: string): string => {
        if (!ends) {
            return 'no cpi'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `cpi expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `cpi expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return 'cpi in good standing'
    }

    private insClass = (ends: string): string => {
        if (!ends) {
            return 'secondary'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 30) {
            return 'danger'
        }

        if (days <= 60) {
            return 'warning'
        }

        return 'success'
    }

    private insMessage = (provider: string, ends: string): string => {
        if (!ends) {
            return 'no insurance'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `ins. expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `ins. expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return `outside ins. active`
    }

    /**
     * Determine an tab icon to show based on the payment category.
     * @param category The payment category.
     */
    protected icon = (category: string) => {
        if (!category || (category.length <= 0)) {
            return 'monetization_on'
        }

        switch(category.toLowerCase()) {
            case 'mv loan':
            case 'initial down':
            case 'deferred down':
            case 'deposit':
                return 'directions_car'
            case 'service':
                return 'car_repair'
            default:
                return 'monetization_on'
        }
    }

    protected dueToClass = (due: string): string => {
        switch(ago(due)) {
            case 'past':
                return 'past'
            default:
                return ''
        }
    }

    public daysPastDue = (due: string): number => {
        return daysAgo(due)
    }

    /**
     * A payment in the list was clicked. 
     * @param payment The payment object that was chosen by the user.
     */
    protected selected = (e: Event, payment: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!payment || !payment['termsId']) {
            return
        }

        const meta = {
            termsId: parseInt(payment['termsId']),
            saleId: parseInt(payment['saleId']),
            contactId: parseInt(payment['contactId'])
        }

        this.termsId = meta.termsId
        this.accountChosen.emit(meta)
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qParam = this.route.snapshot.queryParamMap.get('q')
        if (qParam && (qParam.length > 0)) {
            this.search.get('inquiry').setValue(qParam, {emitEvent: false})
        }

        const qFilter = sessionStorage.getItem(this.FILTER_INQUIRY_KEY)
        if (!qParam && qFilter && (qFilter.length > 0)) {
            this.search.get('inquiry').setValue(qFilter, {emitEvent: false})
        }

        const typeParam = parseInt(this.route.snapshot.queryParamMap.get('type'))
        if (typeParam && !isNaN(typeParam) && (typeParam > 0)) {
            this.search.get('type').setValue(typeParam, {emitEvent: false})
        }

        const typeFilter = sessionStorage.getItem(this.FILTER_TYPE_KEY)
        if (!typeParam && typeFilter && (typeFilter.length > 0)) {
            this.search.get('type').setValue(typeFilter.toString(), {emitEvent: false})
        }

        const statusParam = this.route.snapshot.queryParamMap.get('status')
        if (statusParam && (statusParam.length > 0) && (this.TAB_NAMES.includes(statusParam.toString()))) {
            this.activeTab = statusParam
        }

        const tabFilter = sessionStorage.getItem(this.FILTER_TAB_KEY)
        if (!statusParam && tabFilter && (tabFilter.length > 0) && (this.TAB_NAMES.includes(tabFilter.toString()))) {
            this.activeTab = tabFilter.toString()
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = this.search.get('inquiry').value
        if (!qFilter || (qFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_INQUIRY_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_INQUIRY_KEY, qFilter)
        }

        const typeFilter = this.search.get('type').value
        if (!typeFilter || (typeFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_TYPE_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_TYPE_KEY, typeFilter)
        }

        const tabFilter = this.activeTab
        if (!tabFilter || (tabFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_TAB_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_TAB_KEY, tabFilter)
        }
    }

    /**
     * User has selected the active tab
     * @param e Event that trigger the tab change
     * @param tab The tab the user wants to select (active, inactive)
     */
    protected setActiveTab = (e: Event, tab: string) => {
        if (e) {
            e.stopPropagation()
            e.preventDefault()
        }

        if (!tab || !this.TAB_NAMES.includes(tab)) {
            return
        }

        if (tab === this.activeTab) { // user clicked the active one?
            return
        }

        this.activeTab = tab
        this.writeFilters()
        this.reset()
        this.fetch()
    }
}
